<template>
  <v-card tile>
    <v-card-title>{{ isNew ? "Nueva firma" : "Editar firma" }}</v-card-title>
    <v-card-text>
      <v-text-field
        v-model="form.name"
        label="Nombre"
        :disabled="saving"
        @keyup.enter="save"
      />

      <div class="body-2 mt-2 mb-1">Texto de la firma</div>
      <vue-editor v-model="form.bodyHtml" :editor-toolbar="editorToolbar" />

      <v-file-input
        label="Logo (opcional, hasta 200 KB)"
        accept="image/png,image/jpeg,image/gif"
        :error-messages="logoError"
        :disabled="saving || readingLogo"
        :loading="readingLogo"
        @change="onLogoSelected"
        show-size
      />
      <!-- Mientras se lee el archivo todavia no hay nada nuevo en form.logo:
           sin este texto, el boton Guardar deshabilitado un instante no se
           explica por si solo. -->
      <div v-if="readingLogo" class="body-2 grey--text">Leyendo el logo…</div>
      <img v-if="logoPreview" :src="logoPreview" style="max-height:80px" />
      <v-btn v-if="form.logo" text small :disabled="saving || readingLogo" @click="clearLogo">Quitar logo</v-btn>

      <v-alert v-if="errorMessage" type="error" dense text>{{ errorMessage }}</v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn :disabled="saving" @click="cancel">Cancelar</v-btn>
      <v-btn
        class="primary"
        :loading="saving"
        :disabled="!form.name || saving || readingLogo"
        @click="save"
      >Guardar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
"use strict";

import { VueEditor } from "vue2-editor";
import emailSignatureService from "@/services/EmailSignatureService";

// El backend valida lo mismo derivando el tamaño real del base64: esta
// validacion es para no mandar 200 KB al servidor solo para que los rechace,
// no para reemplazarla.
const MAX_LOGO_BYTES = 200 * 1024;

export default {
  name: "edit-email-signature",
  components: { VueEditor },
  props: {
    signature: { type: Object, required: true }
  },
  data() {
    return {
      // Copia profunda con logo/bodyHtml siempre presentes: si `logo` faltara
      // del objeto de origen, asignarlo mas tarde con `this.form.logo = x` no
      // dispararia la reactividad de Vue 2, que no detecta la creacion de una
      // propiedad de nivel superior que nunca existio en data().
      form: Object.assign({ bodyHtml: "", logo: null }, JSON.parse(JSON.stringify(this.signature))),
      logoPreview: this.signature.logo
        ? `data:${this.signature.logo.mimeType};base64,${this.signature.logo.contentBase64}`
        : null,
      logoError: null,
      errorMessage: null,
      saving: false,
      // En vuelo mientras el FileReader esta leyendo: sin esto, guardar justo
      // despues de elegir el archivo sale con el logo anterior (o ninguno) en
      // silencio, porque onLogoSelected todavia no termino de escribir
      // form.logo cuando save() ya leyo el form.
      readingLogo: false,
      editorToolbar: [
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline"],
        [{ color: [] }],
        ["link"],
        [{ align: [] }],
        ["clean"]
      ]
    };
  },
  computed: {
    isNew() {
      return !this.signature._id;
    }
  },
  methods: {
    onLogoSelected(file) {
      this.logoError = null;
      if (!file) { this.clearLogo(); return; }
      if (file.size > MAX_LOGO_BYTES) {
        this.logoError = "El logo no puede superar los 200 KB";
        return;
      }
      const reader = new FileReader();
      this.readingLogo = true;
      reader.onload = () => {
        // readAsDataURL da "data:image/png;base64,XXXX": el modelo guarda solo
        // la parte de despues de la coma.
        const dataUri = reader.result;
        this.form.logo = {
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          contentBase64: dataUri.substring(dataUri.indexOf(",") + 1)
        };
        this.logoPreview = dataUri;
        this.readingLogo = false;
      };
      reader.onerror = () => {
        // Se libera la bandera y se avisa, pero form.logo (y su preview) no se
        // tocan: si ya habia un logo cargado, sigue ahi tal cual estaba.
        this.logoError = "No se pudo leer el archivo";
        this.readingLogo = false;
      };
      reader.readAsDataURL(file);
    },
    // Explicito y nunca `undefined`: el dominio del backend hace un `$set`
    // parcial donde una clave ausente del payload significa "no tocar" y un
    // `null` explicito significa "borrar". Si este metodo dejara `form.logo`
    // en `undefined`, el boton dejaria de borrar el logo silenciosamente.
    clearLogo() {
      this.form.logo = null;
      this.logoPreview = null;
    },
    cancel() {
      this.$emit("cancel");
    },
    async save() {
      if (!this.form.name || this.saving || this.readingLogo) return;
      this.errorMessage = null;
      this.saving = true;
      try {
        const payload = { name: this.form.name, bodyHtml: this.form.bodyHtml, logo: this.form.logo };
        const saved = this.isNew
          ? await emailSignatureService.create(payload)
          : await emailSignatureService.edit(this.signature._id, payload);
        this.$emit("saved", saved);
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.message) ||
          "No se pudo guardar la firma";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
